import React, { createContext, useState, useEffect } from 'react';
import { AuthContextProps } from './types';
import { useCookies } from 'react-cookie';
import { ILoginDto } from '../../../shared/services/auth/dtos/login-dto';
import { AUTH_TOKEN, FCM_TOKEN_KEY } from '../../../shared/constants/AppConst';
import { authService } from '../../../shared/services/auth';
import { notifier } from '../../../shared/functions/notifier';
import { UserAuthorizationDataDto } from 'src/shared/services/auth/dtos/user-authorization-data-dto';
import { IResetPassword } from 'src/shared/services/auth/dtos/reset-password';
import { normalizeApiError } from 'src/shared/utils/base-api/api-error';

export const AuthContext = createContext<AuthContextProps>({});

// Page state kept for the signed-in account (e.g. the products list page and
// filters) must not carry over to whoever signs in next.
const clearAccountPageState = () => {
  try {
    sessionStorage.clear();
  } catch {
    // Storage unavailable: nothing was kept.
  }
};

const AuthContextProvider = (props: { children: React.ReactNode }) => {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleLogout, setVisibleLogout] = useState<boolean>(false);
  const [responseError, setError] = useState<string>();
  const [cookies, setCookies, removeCookies] = useCookies([AUTH_TOKEN]);
  const [userId] = useState<string>();
  const [permissions, setPermissions] = useState<
    Array<UserAuthorizationDataDto>
  >([]);
  const [userRolesName, setUserRolesName] = useState<Array<string>>([]);
  // False until the signed-in user's roles are known (or failed to load):
  // what depends on them (a shop admin's shop name) waits instead of
  // showing the generic "Admin" first.
  const [rolesLoaded, setRolesLoaded] = useState<boolean>(false);

  useEffect(() => {
    setRolesLoaded(false);
    if (cookies[AUTH_TOKEN]) {
      const loadAuthorizationData = async () => {
        try {
          const response = await authService.getUserAuthorizationData();
          setAuthenticated(Boolean(response));
          setUserRolesName(response?.map((item) => item.SystemName) ?? []);
          setPermissions(response ?? []);
        } catch (error: unknown) {
          console.error(error);
          setAuthenticated(false);
        } finally {
          setRolesLoaded(true);
        }
      };

      void loadAuthorizationData();
    } else {
      setAuthenticated(false);
      clearAccountPageState();
    }
  }, [cookies[AUTH_TOKEN]]);

  const logout = async () => {
    setLoading(true);
    removeCookies(AUTH_TOKEN, { path: '/', sameSite: true });
    setVisibleLogout(false);
    setLoading(false);
    localStorage.removeItem(FCM_TOKEN_KEY);
    clearAccountPageState();
  };

  const login = async (values: ILoginDto): Promise<void> => {
    try {
      setLoading(true);
      const response = await authService.login(values);

      response.errors?.forEach((error) => notifier(error, 'error'));
      if (response.IsActive && response.Token) {
        clearAccountPageState();
        setCookies(AUTH_TOKEN, response.Token, {
          path: '/',
          sameSite: true,
          expires: new Date(response.ExpireDateInUTc),
        });
      }
    } catch (error: unknown) {
      notifier(normalizeApiError(error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const recoverPassword = async (values: IResetPassword): Promise<void> => {
    try {
      setLoading(true);
      await authService.resetPassword(values);
      setError('');
      window.location.href = '/sign-in';
    } catch (error: unknown) {
      setError(normalizeApiError(error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        responseError: responseError,
        loading: loading,
        visibleLogout: visibleLogout,
        setVisibleLogout: setVisibleLogout,
        logout: logout,
        login: login,
        recoverPassword: recoverPassword,
        isAuthenticated: authenticated,
        setAuthenticated: setAuthenticated,
        userId: userId,
        permissions: permissions,
        userRolesName: userRolesName,
        rolesLoaded: rolesLoaded,
        checkingSession: Boolean(cookies[AUTH_TOKEN]) && !rolesLoaded,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
